import { 
  Box, 
  Button,
  Card, 
  CardContent, 
  Grid, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Paper, 
  Typography,
  Breadcrumbs,
  Link as MuiLink
} from "@mui/material";
import { IResourceComponentsProps } from "@refinedev/core";
import React, { useState, useEffect } from "react";
import { ReceiptLong, CreditCard } from "@mui/icons-material";
import { ReceiptsReport } from "../reporting/receipts";
import { CreditsReport } from "../reporting/credits";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import html2pdf from 'html2pdf.js';
import { Download } from "@mui/icons-material";

export const ReportingDashboard: React.FC<IResourceComponentsProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  
  // Check if there's a report specified in the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const report = params.get('report');
    if (report && (report === 'receipts' || report === 'credits' || report === 'dashboard')) {
      setSelectedReport(report);
    }
  }, [location]);

  // Function to handle PDF export
  const handleExportPDF = async () => {
    const element = document.getElementById('report-content');
    if (!element) return;

    const opt = {
      margin: [0.5, 0.5],
      filename: `${selectedReport}-report-${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'a4', 
        orientation: 'portrait' as const
      }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const renderReportContent = () => {
    switch (selectedReport) {
      case "receipts":
        return (
          <Box>
            <Box display="flex" justifyContent="flex-end" mb={3}>
              <Button 
                variant="outlined" 
                endIcon={<ArrowRightIcon />} 
                onClick={() => {
                  setSelectedReport(null);
                  navigate('/reporting/receipts');
                }}
              >
                {t("See in page")}
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExportPDF}
              >
                {t("Export PDF")}
              </Button>
            </Box>
            <div id="report-content" style={{ padding: '20px', background: '#fff' }}>
              <Box mb={3}>
                <Typography variant="h5">{t("Receipts Report")}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {new Date().toLocaleDateString()}
                </Typography>
              </Box>
              <ReceiptsReport />
            </div>
          </Box>
        );
      case "credits":
        return (
          <Box>
            <Box display="flex" justifyContent="flex-end" mb={3}>
              <Button 
                variant="outlined" 
                endIcon={<ArrowRightIcon />} 
                onClick={() => {
                  setSelectedReport(null);
                  navigate('/reporting/credits');
                }}
              >
                {t("See in page")}
              </Button>
            </Box>
            <div id="report-content" style={{ padding: '20px', background: '#fff' }}>
              <CreditsReport />
            </div>
          </Box>
        );
      default:
        return (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            height="100%" 
            p={4}
          >
            <Typography variant="h5" color="textSecondary">
              {t("Select a report from the menu")}
            </Typography>
          </Box>
        );
    }
  };

  // Handle navigation to a specific report
  const navigateToReport = (report: string) => {
    setSelectedReport(report);
    navigate(`/reporting?report=${report}`);
  };
  
  return (
    <Box p={2}>
      <Box mb={3}>
        <Breadcrumbs aria-label="breadcrumb">
          <MuiLink component={Link} to="/" color="inherit">
            {t("Home")}
          </MuiLink>
          <Typography color="text.primary">{t("Reporting Dashboard")}</Typography>
          {selectedReport && (
            <Typography color="text.primary">
              {selectedReport === 'receipts' ? t("Receipts Report") : 
               selectedReport === 'credits' ? t("Credits Report") : 
               t("Financial Dashboard")}
            </Typography>
          )}
        </Breadcrumbs>
      </Box>
      
      <Typography variant="h4" gutterBottom>
        {t("Reporting Dashboard")}
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper elevation={2}>
            <List component="nav" aria-label="reports menu">
              <ListItem>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("Reports")}
                </Typography>
              </ListItem>
              
              <ListItemButton 
                selected={selectedReport === "receipts"}
                onClick={() => navigateToReport("receipts")}
              >
                <ListItemIcon>
                  <ReceiptLong />
                </ListItemIcon>
                <ListItemText primary={t("Receipts Report")} />
              </ListItemButton>
              
              <ListItemButton 
                selected={selectedReport === "credits"}
                onClick={() => navigateToReport("credits")}
              >
                <ListItemIcon>
                  <CreditCard />
                </ListItemIcon>
                <ListItemText primary={t("Credits Report")} />
              </ListItemButton>
            </List>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={9}>
          <Card>
            <CardContent>
              {renderReportContent()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
